import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getAssetsNames } from 'services/assets-service';
import { getDatas } from 'services/datas-service';
import {
  addProcessDataMappingBatch,
  editProcessDataMapping,
  getProcessById,
  getProcessMappingByIdAndMappingId,
} from 'services/processes-service';
import { getLegalBases, getDataCategories } from 'services/mapping-service';
import { getSystemsNames } from 'services/systems-service';
import { useAuthTenant } from 'store/auth';
import { useEffect, useState } from 'react';
import { Main as Container } from 'Components/Main';
import { Dialog, PageHeader, Select, TextField } from 'Components/ui';
import { FormSuccess } from 'Components/Form/Success';
import { treatments } from './helpers';
import styled from 'styled-components';

// ─── Constants ────────────────────────────────────────────────────────────────

const TREATMENT_LABEL_KEY = {
  Criar: 'create',
  Armazenar: 'store',
  Acessar: 'access',
  Compartilhar: 'share',
  Arquivar: 'archive',
  Destruir: 'destroy',
};

const MAX_CUSTOM_TEXT_LENGTH = 500;

const TREATMENT_GRADIENTS = [
  'linear-gradient(135deg,var(--vo-success),color-mix(in srgb, var(--vo-success) 80%, white))',
  'linear-gradient(135deg,var(--vo-warning),var(--vo-warning))',
  'linear-gradient(135deg,var(--vo-celeste),var(--vo-capri))',
  'linear-gradient(135deg,var(--vo-purple-600),color-mix(in srgb, var(--vo-purple) 60%, white))',
  'linear-gradient(135deg,var(--vo-danger),var(--vo-warning))',
];

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`;

const CancelBtn = styled.button`
  height: 44px;
  padding: 0 20px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--vo-celeste);
  background: white;
  border: 1.5px solid var(--vo-celeste);
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: var(--vo-mist); }
`;

const SaveBtn = styled.button`
  height: 44px;
  padding: 0 24px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: var(--vo-celeste);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: var(--vo-cobalto); }
`;

const FormWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 800px;
`;

const FormCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $spaceBetween }) => ($spaceBetween ? 'space-between' : 'flex-start')};
  gap: 10px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const CardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CardIconBox = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CardTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;

const CardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

const RemoveBtn = styled.button`
  height: 36px;
  background: transparent;
  color: var(--vo-danger);
  border: 1px solid var(--vo-danger);
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:hover { background: color-mix(in srgb, var(--vo-danger) 6%, transparent); }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${({ $fullWidth }) => $fullWidth && 'grid-column: 1 / -1;'}
`;

const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
`;

const RequiredStar = styled.span`
  color: var(--vo-danger);
`;

const ErrorMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  color: var(--vo-danger);
  margin: 2px 0 0;
`;

const AddTreatmentBtn = styled.button`
  height: 48px;
  background: white;
  color: var(--vo-celeste);
  border: 1.5px dashed var(--vo-celeste);
  border-radius: 12px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover { background: var(--vo-capri-50); }
`;

// Shimmer for loading selects
const ShimmerBox = styled.div`
  height: 48px;
  border-radius: 10px;
  background: linear-gradient(90deg, var(--vo-mist) 25%, var(--vo-cloud) 50%, var(--vo-mist) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const DataMapping = () => {
  const { t, i18n } = useTranslation(['mapping']);
  const { id, mappingId } = useParams();
  const navigate = useNavigate();
  const [finished, setFinished] = useState(false);
  const selectedTenant = useSelector(useAuthTenant);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      // Add mode multi-selects data items (array); edit mode targets exactly
      // one already-saved record, so it keeps today's single-value name.
      name: mappingId ? '' : [],
      category: '',
      customDataCategory: '',
      dataSource: '',
      holderProfile: '',
      retentionPeriod: '',
      storage: '',
      dataProcessing: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'dataProcessing',
  });

  const handleAddDataProcessing = () => {
    append({
      processing: [],
      shareType: '',
      legalBase: '',
      customLegalBase: '',
      complementLegalBasis: '',
      finality: '',
      position: '',
      tool: '',
      observation: '',
      destiny: '',
    });
  };

  // Explodes one treatment card's multi-selected `processing` types into one
  // DataProcessingModel entry per type, cloning every other field (shared
  // across the card) as-is. shareType/destiny are only ever meaningful for
  // the 'Compartilhar' type — attaching them to every exploded entry would
  // silently mislabel the other treatment types in the same card as shares,
  // so they're kept undefined on every entry except that one.
  const explodeTreatmentCard = dp => {
    const types = Array.isArray(dp.processing) ? dp.processing : [dp.processing].filter(Boolean);
    return types.map(type => ({
      processing: type,
      legalBase: dp.legalBase,
      customLegalBase: dp.legalBase === 'other' ? dp.customLegalBase : undefined,
      complementLegalBasis: dp.complementLegalBasis,
      finality: dp.finality,
      position: dp.position,
      tool: dp.tool,
      observation: dp.observation,
      shareType: type === 'Compartilhar' ? dp.shareType : undefined,
      destiny: type === 'Compartilhar' ? dp.destiny : undefined,
    }));
  };

  const buildDataProcessing = dataProcessing =>
    (dataProcessing ?? []).flatMap(explodeTreatmentCard);

  // Card 1 fields shared by every generated record, independent of which
  // (possibly several) data items they end up attached to.
  const buildSharedFields = dados => ({
    category: dados.category,
    customDataCategory: dados.category === 'other' ? dados.customDataCategory : undefined,
    dataSource: dados.dataSource,
    holderProfile: dados.holderProfile,
    retentionPeriod: dados.retentionPeriod,
    storage: dados.storage,
  });

  const onSubmit = async () => {
    try {
      const dados = getValues();
      const dataProcessing = buildDataProcessing(dados.dataProcessing);
      const sharedFields = buildSharedFields(dados);

      if (mappingId) {
        // Edit mode always targets the single existing record at this index.
        const formattedForm = { name: dados.name, ...sharedFields, dataProcessing };
        const res = await editProcessDataMapping(id, mappingId, formattedForm);
        if (res.status !== 200) {
          throw new Error(t('mapping:dataMapping.errorDialog.defaultMessage'));
        }
      } else {
        // Add mode: one record per selected data item, all sharing the same
        // Card 1 fields and the same exploded treatment list - fanned out
        // client-side, then submitted as one atomic batch so the backend
        // does a single read/mutate/write instead of racing N separate ones.
        const batch = (dados.name ?? []).map(selectedName => ({
          name: selectedName,
          ...sharedFields,
          dataProcessing,
        }));
        const res = await addProcessDataMappingBatch(id, batch);
        if (res.status !== 200) {
          throw new Error(t('mapping:dataMapping.errorDialog.defaultMessage'));
        }
      }
      setFinished(true);
    } catch (error) {
      Dialog.alert({
        title: t('mapping:dataMapping.errorDialog.title'),
        message: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const handleReset = () => {
    reset();
    navigate(`/processes/show/${id}`);
  };

  // ── Queries ──────────────────────────────────────────────────────────────────

  const { data: processData } = useQuery(['getProcessById', id], async () => {
    const res = await getProcessById(id);
    return res?.data ?? null;
  });

  const { data: legalBasesData, isLoading: legalBasesLoading, isError: legalBasesError } = useQuery(
    ['getLegalBases', selectedTenant?.slug, i18n.language],
    async () => {
      const result = await getLegalBases(selectedTenant?.slug);
      return result?.data?.data ?? [];
    },
  );

  const { data: dataCategoriesData, isLoading: dataCategoriesLoading, isError: dataCategoriesError } = useQuery(
    ['getDataCategories', selectedTenant?.slug, i18n.language],
    async () => {
      const result = await getDataCategories(selectedTenant?.slug);
      return result?.data?.data ?? [];
    },
  );

  const queries = useQueries([
    {
      queryKey: ['getSystemsNames'],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getSystemsNames(selectedTenant.slug);
        return result?.data?.length > 0 ? result.data : [];
      },
    },
    {
      queryKey: ['getAssetsNames'],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getAssetsNames(selectedTenant.slug);
        return result?.data?.length > 0 ? result.data : [];
      },
    },
    {
      queryKey: ['getDatas'],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getDatas(selectedTenant.slug);
        return result?.data?.length > 0 ? result.data : [];
      },
    },
  ]);

  const [
    { data: systems, isLoading: systemLoading, isError: systemError },
    { data: assets, isLoading: assetLoading, isError: assetError },
    { data: datas, isLoading: dataLoading, isError: dataError },
  ] = queries;

  const { isLoading, data: dataMapping } = useQuery(
    ['getProcessMapping', id, mappingId],
    async () => {
      if (!id || !mappingId) return null;
      const res = await getProcessMappingByIdAndMappingId(id, mappingId);
      return res.data ?? null;
    },
  );

  useEffect(() => {
    if (!id || !mappingId || !dataMapping) return;
    Object.keys(dataMapping).forEach(key => {
      // Saved records still store `processing` as the single scalar type
      // today's DB shape always had; the multi-select needs it as an array
      // to show the existing selection, same as any other multi-select field.
      if (key === 'dataProcessing' && Array.isArray(dataMapping.dataProcessing)) {
        setValue(
          'dataProcessing',
          dataMapping.dataProcessing.map(dp => ({
            ...dp,
            processing: dp.processing ? [dp.processing] : [],
          })),
        );
        return;
      }
      setValue(key, dataMapping[key]);
    });
  }, [id, mappingId, dataMapping, setValue]);

  const processing = watch('dataProcessing');
  const watchCategory = watch('category');

  const sortedCategories = [
    ...(dataCategoriesData ?? []).filter(item => item.key !== 'other'),
    ...(dataCategoriesData ?? []).filter(item => item.key === 'other'),
  ];

  const sortedLegalBases = [
    ...(legalBasesData ?? []).filter(item => item.key !== 'other'),
    ...(legalBasesData ?? []).filter(item => item.key === 'other'),
  ];

  // ── Render ───────────────────────────────────────────────────────────────────

  if (isLoading) return null;

  return (
    <Container>
      <PageWrap>
        {/* Header */}
        <PageHeader
          title={mappingId ? t('mapping:dataMapping.title.edit') : t('mapping:dataMapping.title.add')}
          segments={[
            { label: t('common:breadcrumbs.mapping') },
            { label: t('mapping:processes.list.title'), route: '/processes' },
            { label: processData?.name, route: `/processes/show/${id}` },
            { label: t('mapping:dataMapping.breadcrumbLabel') },
          ]}
          actions={
            !finished && (
              <>
                <CancelBtn type="button" onClick={handleReset}>
                  {t('mapping:dataMapping.actions.cancel')}
                </CancelBtn>
                <SaveBtn type="submit" form="data-mapping-form">
                  {t('mapping:dataMapping.actions.save')}
                </SaveBtn>
              </>
            )
          }
        />

        {!finished ? (
          <form id="data-mapping-form" onSubmit={handleSubmit(onSubmit)}>
            <FormWrap>
              {/* ── Card 1: Dado pessoal mapeado ── */}
              <FormCard>
                <CardHeader>
                  <CardHeaderLeft>
                    <CardIconBox $gradient="linear-gradient(135deg,var(--vo-cobalto),var(--vo-capri))">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <ellipse cx="12" cy="5" rx="9" ry="3" />
                        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                      </svg>
                    </CardIconBox>
                    <CardTitleWrap>
                      <CardTitle>{t('mapping:dataMapping.card1Title')}</CardTitle>
                      <CardSubtitle>
                        {processData?.name
                          ? t('mapping:dataMapping.card1Subtitle', { name: processData.name })
                          : t('mapping:dataMapping.breadcrumb')}
                      </CardSubtitle>
                    </CardTitleWrap>
                  </CardHeaderLeft>
                </CardHeader>

                <FieldGrid>
                  {/* Tipo de dado */}
                  <FieldGroup data-tour="data-name">
                    <FieldLabel>
                      {t('mapping:dataMapping.fields.data')}<RequiredStar> *</RequiredStar>
                    </FieldLabel>
                    {dataLoading || dataError ? (
                      <ShimmerBox />
                    ) : mappingId ? (
                      // Edit mode targets exactly one already-saved record — keep
                      // today's single-select behavior unchanged.
                      <Controller
                        control={control}
                        name="name"
                        rules={{
                          validate: () => getValues('name') !== '',
                          required: t('mapping:dataMapping.errors.required.data'),
                        }}
                        render={({ field: { onChange, value }, fieldState }) => (
                          <>
                            <Select
                              name="name"
                              error={!!fieldState.error}
                              value={value}
                              onChange={onChange}
                              options={[
                                { value: '', label: t('mapping:dataMapping.placeholders.data') },
                                ...(datas || []).map(item => ({ value: item.name, label: item.name })),
                              ]}
                            />
                            {fieldState.error && <ErrorMsg>{fieldState.error.message}</ErrorMsg>}
                          </>
                        )}
                      />
                    ) : (
                      // Add mode: pick several data items at once — one record per
                      // item gets fanned out at submit time (see onSubmit).
                      <Controller
                        control={control}
                        name="name"
                        rules={{
                          validate: v => Array.isArray(v) && v.length > 0,
                          required: t('mapping:dataMapping.errors.required.data'),
                        }}
                        render={({ field: { onChange, value }, fieldState }) => (
                          <>
                            <Select
                              name="name"
                              multiple
                              error={!!fieldState.error}
                              value={value ?? []}
                              onChange={onChange}
                              options={(datas || []).map(item => ({ value: item.name, label: item.name }))}
                            />
                            {fieldState.error && <ErrorMsg>{fieldState.error.message}</ErrorMsg>}
                          </>
                        )}
                      />
                    )}
                  </FieldGroup>

                  {/* Categoria */}
                  <FieldGroup>
                    <FieldLabel>
                      {t('mapping:data_categories.label')}<RequiredStar> *</RequiredStar>
                    </FieldLabel>
                    {dataCategoriesLoading ? (
                      <ShimmerBox />
                    ) : (
                      <Controller
                        control={control}
                        name="category"
                        rules={{
                          validate: () => getValues('category') !== '',
                          required: t('mapping:dataMapping.errors.required.category'),
                        }}
                        render={({ field: { onChange, value }, fieldState }) => (
                          <>
                            <Select
                              name="category"
                              error={!!fieldState.error}
                              value={value}
                              onChange={onChange}
                              disabled={dataCategoriesError}
                              options={[
                                {
                                  value: '',
                                  label: dataCategoriesError
                                    ? t('mapping:data_categories.error')
                                    : t('mapping:data_categories.placeholder'),
                                },
                                ...sortedCategories.map(item => ({ value: item.key, label: item.name })),
                              ]}
                            />
                            {fieldState.error && <ErrorMsg>{fieldState.error.message}</ErrorMsg>}
                          </>
                        )}
                      />
                    )}
                  </FieldGroup>

                  {/* Custom category — spans full width when visible */}
                  {watchCategory === 'other' && (
                    <FieldGroup $fullWidth>
                      <FieldLabel>
                        {t('mapping:data_categories.custom_label')}<RequiredStar> *</RequiredStar>
                      </FieldLabel>
                      <TextField
                        fullWidth
                        error={!!errors?.customDataCategory}
                        placeholder={t('mapping:data_categories.custom_placeholder')}
                        {...register('customDataCategory', {
                          required: t('mapping:data_categories.custom_required'),
                          validate: v => (!v?.trim() ? t('mapping:data_categories.custom_required') : true),
                          maxLength: {
                            value: MAX_CUSTOM_TEXT_LENGTH,
                            message: t('mapping:data_categories.custom_max_length', { count: MAX_CUSTOM_TEXT_LENGTH }),
                          },
                        })}
                      />
                      {errors?.customDataCategory && <ErrorMsg>{errors.customDataCategory.message}</ErrorMsg>}
                    </FieldGroup>
                  )}

                  {/* Origem dos dados */}
                  <FieldGroup>
                    <FieldLabel>
                      {t('mapping:dataMapping.fields.dataSource')}<RequiredStar> *</RequiredStar>
                    </FieldLabel>
                    <TextField
                      fullWidth
                      error={!!errors?.dataSource}
                      placeholder={t('mapping:dataMapping.placeholders.dataSource')}
                      {...register('dataSource', { required: t('mapping:dataMapping.errors.required.dataSource') })}
                    />
                    {errors?.dataSource && <ErrorMsg>{errors.dataSource.message}</ErrorMsg>}
                  </FieldGroup>

                  {/* Perfil do titular */}
                  <FieldGroup>
                    <FieldLabel>
                      {t('mapping:dataMapping.fields.holderProfile')}<RequiredStar> *</RequiredStar>
                    </FieldLabel>
                    <TextField
                      fullWidth
                      error={!!errors?.holderProfile}
                      placeholder={t('mapping:dataMapping.placeholders.holderProfile')}
                      {...register('holderProfile', { required: t('mapping:dataMapping.errors.required.holderProfile') })}
                    />
                    {errors?.holderProfile && <ErrorMsg>{errors.holderProfile.message}</ErrorMsg>}
                  </FieldGroup>

                  {/* Período de retenção */}
                  <FieldGroup>
                    <FieldLabel>{t('mapping:dataMapping.fields.retentionPeriod')}</FieldLabel>
                    <TextField
                      fullWidth
                      placeholder={t('mapping:dataMapping.placeholders.retentionPeriod')}
                      {...register('retentionPeriod')}
                    />
                  </FieldGroup>

                  {/* Armazenamento */}
                  <FieldGroup>
                    <FieldLabel>
                      {t('mapping:dataMapping.fields.storage')}<RequiredStar> *</RequiredStar>
                    </FieldLabel>
                    {systemLoading || assetLoading ? (
                      <ShimmerBox />
                    ) : (
                      <Controller
                        control={control}
                        name="storage"
                        rules={{
                          validate: () => getValues('storage') !== '',
                          required: t('mapping:dataMapping.errors.required.storage'),
                        }}
                        render={({ field: { onChange, value }, fieldState }) => (
                          <>
                            <Select
                              name="storage"
                              error={!!fieldState.error}
                              value={value}
                              onChange={onChange}
                              disabled={systemError || assetError}
                              options={[
                                { value: '', label: t('mapping:dataMapping.placeholders.storage') },
                                ...(systems || []).map(item => ({ value: item.name, label: item.name })),
                                ...(assets || []).map(item => ({ value: item.name, label: item.name })),
                              ]}
                            />
                            {fieldState.error && <ErrorMsg>{fieldState.error.message}</ErrorMsg>}
                          </>
                        )}
                      />
                    )}
                  </FieldGroup>
                </FieldGrid>
              </FormCard>

              {/* ── Treatment cards ── */}
              {fields.map((itemProcess, indexProcess) => {
                const gradient = TREATMENT_GRADIENTS[indexProcess % TREATMENT_GRADIENTS.length];
                const watchProcessing = processing?.[indexProcess]?.processing;
                const watchIsShare = Array.isArray(watchProcessing) && watchProcessing.includes('Compartilhar');
                const watchLegalBase = watch(`dataProcessing.${indexProcess}.legalBase`);

                return (
                  <FormCard key={itemProcess.id}>
                    <CardHeader $spaceBetween>
                      <CardHeaderLeft>
                        <CardIconBox $gradient={gradient}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          </svg>
                        </CardIconBox>
                        <CardTitleWrap>
                          <CardTitle>
                            {t('mapping:dataMapping.treatmentIndex', { index: indexProcess + 1 })}
                          </CardTitle>
                          <CardSubtitle>{t('mapping:dataMapping.treatmentSubtitle')}</CardSubtitle>
                        </CardTitleWrap>
                      </CardHeaderLeft>
                      <RemoveBtn type="button" onClick={() => remove(indexProcess)}>
                        {t('mapping:dataMapping.actions.removeTreatment')}
                      </RemoveBtn>
                    </CardHeader>

                    <FieldGrid>
                      {/* Tipo de tratamento — multi-select: each selected type is
                          exploded into its own DataProcessing entry at submit time,
                          all sharing this card's other fields (see explodeTreatmentCard). */}
                      <FieldGroup>
                        <FieldLabel>
                          {t('mapping:dataMapping.fields.treatment')}<RequiredStar> *</RequiredStar>
                        </FieldLabel>
                        <Controller
                          control={control}
                          name={`dataProcessing.${indexProcess}.processing`}
                          rules={{
                            validate: v => Array.isArray(v) && v.length > 0,
                            required: t('mapping:dataMapping.errors.required.treatment'),
                          }}
                          render={({ field: { onChange, value }, fieldState }) => (
                            <>
                              <Select
                                name={`dataProcessing.${indexProcess}.processing`}
                                multiple
                                error={!!fieldState.error}
                                value={value ?? []}
                                onChange={onChange}
                                options={treatments.map(item => ({
                                  value: item,
                                  label: TREATMENT_LABEL_KEY[item]
                                    ? t(`mapping:dataMapping.treatments.${TREATMENT_LABEL_KEY[item]}`)
                                    : item,
                                }))}
                              />
                              {fieldState.error && <ErrorMsg>{fieldState.error.message}</ErrorMsg>}
                            </>
                          )}
                        />
                      </FieldGroup>

                      {/* Base legal */}
                      <FieldGroup>
                        <FieldLabel>{t('mapping:legal_bases.label')}</FieldLabel>
                        {legalBasesLoading ? (
                          <ShimmerBox />
                        ) : (
                          <Controller
                            control={control}
                            name={`dataProcessing.${indexProcess}.legalBase`}
                            render={({ field: { onChange, value } }) => (
                              <Select
                                name={`dataProcessing.${indexProcess}.legalBase`}
                                value={value ?? ''}
                                onChange={onChange}
                                disabled={legalBasesError}
                                options={[
                                  {
                                    value: '',
                                    label: legalBasesError
                                      ? t('mapping:legal_bases.error')
                                      : t('mapping:legal_bases.placeholder'),
                                  },
                                  ...sortedLegalBases.map(item => ({ value: item.key, label: item.name })),
                                ]}
                              />
                            )}
                          />
                        )}
                      </FieldGroup>

                      {/* Conditional: shareType + destiny when Compartilhar is among the
                          selected treatment types for this card */}
                      {watchIsShare && (
                        <>
                          <FieldGroup>
                            <FieldLabel>{t('mapping:dataMapping.fields.shareType')}</FieldLabel>
                            <Controller
                              control={control}
                              name={`dataProcessing.${indexProcess}.shareType`}
                              render={({ field: { onChange, value } }) => (
                                <Select
                                  name={`dataProcessing.${indexProcess}.shareType`}
                                  value={value ?? ''}
                                  onChange={onChange}
                                  options={[
                                    { value: '', label: t('mapping:dataMapping.placeholders.shareType') },
                                    { value: 'Interno', label: t('mapping:dataMapping.shareTypes.internal') },
                                    { value: 'Externo', label: t('mapping:dataMapping.shareTypes.external') },
                                  ]}
                                />
                              )}
                            />
                          </FieldGroup>

                          <FieldGroup>
                            <FieldLabel>{t('mapping:dataMapping.fields.destiny')}</FieldLabel>
                            <TextField
                              fullWidth
                              placeholder={t('mapping:dataMapping.placeholders.destiny')}
                              {...register(`dataProcessing.${indexProcess}.destiny`)}
                            />
                          </FieldGroup>
                        </>
                      )}

                      {/* Finalidade */}
                      <FieldGroup>
                        <FieldLabel>{t('mapping:dataMapping.fields.finality')}</FieldLabel>
                        <TextField
                          fullWidth
                          placeholder={t('mapping:dataMapping.placeholders.finality')}
                          {...register(`dataProcessing.${indexProcess}.finality`)}
                        />
                      </FieldGroup>

                      {/* Posição */}
                      <FieldGroup>
                        <FieldLabel>{t('mapping:dataMapping.fields.position')}</FieldLabel>
                        <Controller
                          control={control}
                          name={`dataProcessing.${indexProcess}.position`}
                          render={({ field: { onChange, value } }) => (
                            <Select
                              name={`dataProcessing.${indexProcess}.position`}
                              value={value ?? ''}
                              onChange={onChange}
                              options={[
                                { value: '', label: t('mapping:dataMapping.placeholders.position') },
                                { value: 'Controlador', label: t('mapping:dataMapping.positions.controller') },
                                { value: 'Operador', label: t('mapping:dataMapping.positions.operator') },
                              ]}
                            />
                          )}
                        />
                      </FieldGroup>

                      {/* Ferramenta */}
                      <FieldGroup>
                        <FieldLabel>{t('mapping:dataMapping.fields.tool')}</FieldLabel>
                        {systemLoading || assetLoading ? (
                          <ShimmerBox />
                        ) : (
                          <Controller
                            control={control}
                            name={`dataProcessing.${indexProcess}.tool`}
                            render={({ field: { onChange, value } }) => (
                              <Select
                                name="tool"
                                value={value ?? ''}
                                onChange={onChange}
                                options={[
                                  { value: '', label: t('mapping:dataMapping.placeholders.tool') },
                                  ...(systems || []).map(item => ({ value: item.name, label: item.name })),
                                  ...(assets || []).map(item => ({ value: item.name, label: item.name })),
                                ]}
                              />
                            )}
                          />
                        )}
                      </FieldGroup>

                      {/* Complemento base legal */}
                      <FieldGroup>
                        <FieldLabel>{t('mapping:dataMapping.fields.complementLegalBasis')}</FieldLabel>
                        <TextField
                          fullWidth
                          placeholder={t('mapping:dataMapping.placeholders.complementLegalBasis')}
                          {...register(`dataProcessing.${indexProcess}.complementLegalBasis`)}
                        />
                      </FieldGroup>

                      {/* Custom legal base when legalBase = other */}
                      {watchLegalBase === 'other' && (
                        <FieldGroup>
                          <FieldLabel>
                            {t('mapping:legal_bases.custom_label')}<RequiredStar> *</RequiredStar>
                          </FieldLabel>
                          <TextField
                            fullWidth
                            error={!!errors?.dataProcessing?.[indexProcess]?.customLegalBase}
                            placeholder={t('mapping:legal_bases.custom_placeholder')}
                            {...register(`dataProcessing.${indexProcess}.customLegalBase`, {
                              required: t('mapping:legal_bases.custom_required'),
                              validate: v => (!v?.trim() ? t('mapping:legal_bases.custom_required') : true),
                              maxLength: {
                                value: MAX_CUSTOM_TEXT_LENGTH,
                                message: t('mapping:legal_bases.custom_max_length', { count: MAX_CUSTOM_TEXT_LENGTH }),
                              },
                            })}
                          />
                          {errors?.dataProcessing?.[indexProcess]?.customLegalBase && (
                            <ErrorMsg>{errors.dataProcessing[indexProcess].customLegalBase.message}</ErrorMsg>
                          )}
                        </FieldGroup>
                      )}

                      {/* Observação — full width */}
                      <FieldGroup $fullWidth>
                        <FieldLabel>{t('mapping:dataMapping.fields.observation')}</FieldLabel>
                        <TextField
                          multiline
                          fullWidth
                          rows={2}
                          placeholder={t('mapping:dataMapping.placeholders.observation')}
                          {...register(`dataProcessing.${indexProcess}.observation`)}
                        />
                      </FieldGroup>
                    </FieldGrid>
                  </FormCard>
                );
              })}

              {/* ── Add treatment button ── */}
              <AddTreatmentBtn type="button" onClick={handleAddDataProcessing}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                {t('mapping:dataMapping.actions.addTreatment')}
              </AddTreatmentBtn>
            </FormWrap>
          </form>
        ) : (
          <div style={{ maxWidth: 800 }}>
            <FormCard>
              <FormSuccess
                title={t('mapping:dataMapping.success.title')}
                labelStartAgain={t('mapping:dataMapping.success.addAnother')}
                handleStartAgain={handleReset}
                labelShow={t('mapping:dataMapping.success.viewDetails')}
                handleShow={() => navigate(`/processes/show/${id}`)}
              />
            </FormCard>
          </div>
        )}
      </PageWrap>
    </Container>
  );
};
